import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, IconButton, Stack } from '@mui/material';
import { Button } from 'src/UIKit/components';
import dayjs from 'dayjs';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetDefaultCustomFieldsApi } from 'src/api/customFields';
import {
  useDeleteMessageTemplateApi,
  useGetMessageTemplateApi,
  useGetMessageTemplatesApi,
} from 'src/api/messageTemplates';
import { GetMessageTemplatesParams } from 'src/api/messageTemplates/types';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import GridData from 'src/components/GridData/GridData';
import { TableHeader } from 'src/components/TableHeader/TableHeader';
import TooltipCell from 'src/components/TooltipCell';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { getSortFieldForApi } from 'src/helpers/sort';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { selectProfile } from 'src/modules/profile/slices';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import CreateMessageTemplateModal from './CreateMessageTemplateModal/view';
import { NON_CAMPAIGN_TEMPLATE_VALUE_TYPES } from 'src/constants/templateValueTypes';
import { settingsTabPanelSx } from '../../helpers';

const MessageTemplates = () => {
  const [page, setPage] = useState<number>(0);
  const [sortField, setSortField] = useState<string>('name_asc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [messageTemplates, setMessageTemplates] = useState<{
    count: number;
    items: MessageTemplate[] | null;
  }>({
    count: 0,
    items: null,
  });
  const [editableMessageTemplate, setEditableMessageTemplate] = useState<
    MessageTemplate | undefined
  >();
  const [metadata, setMetadata] = useState<DefaultCustomField[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { profile } = useSelector(selectProfile);
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const getMetadata = useGetDefaultCustomFieldsApi();
  const getMessageTemplates = useGetMessageTemplatesApi();
  const getMessageTemplate = useGetMessageTemplateApi();
  const deleteMessageTemplate = useDeleteMessageTemplateApi();
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const pageSize = 25;
  const isButtonsDisabled = !profile || profile?.role?.name === 'company_user';

  const initialMessageTemplate: MessageTemplate = {
    id: undefined,
    name: '',
    text: '',
  };

  const handleMessageTemplateEdit = (messageTemplateId: string) => {
    if (selectedCompanyId) {
      getMessageTemplate(messageTemplateId, selectedCompanyId).then(response => {
        setEditableMessageTemplate(response.data);
        setShowCreateModal(true);
      });
    }
  };

  const handleMessageTemplateDelete = (messageTemplateId: string) => {
    selectedCompanyId &&
      deleteMessageTemplate(messageTemplateId, selectedCompanyId).then(refreshMessageTemplates);
  };

  const refreshMessageTemplates = () => {
    setEditableMessageTemplate(undefined);
    if (selectedCompanyId) {
      setIsLoading(true);
      const reqQuery: GetMessageTemplatesParams = {
        companyId: selectedCompanyId,
        limit: pageSize,
        offset: page * pageSize,
        sort: `${getSortFieldForApi(sortField)}`,
        status: 'active',
      };
      if (searchQuery) {
        reqQuery.query = searchQuery;
      }
      getMessageTemplates(reqQuery)
        .then(({ data }) => {
          setMessageTemplates(data);
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      const params = {
        companyId: selectedCompanyId,
        types: NON_CAMPAIGN_TEMPLATE_VALUE_TYPES,
      };
      getMetadata(params)
        .then(data => {
          setMetadata(data.data.items);
        })
        .catch(e => {
          console.log(e);
        });
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    selectedCompanyId && refreshMessageTemplates();
  }, [page, selectedCompanyId, sortField, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [sortField, searchQuery]);

  const formatTableRows = (messageTemplates: MessageTemplate[]) => {
    return messageTemplates.map((item: MessageTemplate) => {
      return {
        id: item.id,
        name: item.name,
        text: item.text,
        dateCreated: item.dateCreated,
        dateUpdated: item.dateUpdated,
        actions: 'edit/delete',
      };
    });
  };

  if (!profile) {
    return null;
  }

  return (
    <>
      {metadata && showCreateModal && (
        <CreateMessageTemplateModal
          metadata={metadata}
          initialMessageTemplate={initialMessageTemplate}
          messageTemplate={editableMessageTemplate || initialMessageTemplate}
          show={showCreateModal}
          setShowModal={setShowCreateModal}
          refreshMessageTemplates={refreshMessageTemplates}
        />
      )}

      <Box sx={settingsTabPanelSx}>
        <GridData
          header={
            <TableHeader
              searchValue={searchQuery}
              onSearchChange={(_: ChangeEvent<HTMLInputElement>, query: string) => {
                setSearchQuery(query);
              }}
              actions={
                <Button
                  disabled={isButtonsDisabled}
                  variant="contained"
                  onClick={() => {
                    setEditableMessageTemplate(initialMessageTemplate);
                    setShowCreateModal(true);
                  }}
                >
                  Add New Template
                </Button>
              }
            />
          }
          isLoading={isLoading}
          rows={formatTableRows(messageTemplates.items || [])}
          columns={[
            {
              field: 'name',
              headerName: 'Name',
              minWidth: 200,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="column" height="100%" justifyContent="center">
                    <TooltipCell
                      value={params.value || ''}
                      variant="body2"
                    />
                  </Stack>
                );
              },
            },
            {
              field: 'text',
              headerName: 'Template',
              minWidth: 300,
              editable: false,
              sortable: false,
              flex: 2,
              renderCell: params => {
                return (
                  <Stack direction="column" height="100%" justifyContent="center">
                    <TooltipCell
                      value={params.value || ''}
                      variant="body2"
                    />
                  </Stack>
                );
              },
            },
            {
              field: 'dateCreated',
              headerName: 'Created',
              minWidth: 150,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Box>{params.value ? dayjs(params.value).format('MM/DD/YYYY hh:mm A') : ''}</Box>
                );
              },
            },
            {
              field: 'dateUpdated',
              headerName: 'Updated',
              minWidth: 150,
              editable: false,
              flex: 1,
              renderCell: params => {
                return (
                  <Stack direction="row" justifyContent="space-between">
                    <Box
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {params.value ? dayjs(params.value).format('MM/DD/YYYY hh:mm A') : ''}
                    </Box>
                  </Stack>
                );
              },
            },
          ]}
          renderAction={row => {
            return (
              <>
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={() => row.id && handleMessageTemplateEdit(row.id)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  disabled={isButtonsDisabled}
                  size="small"
                  color="primary"
                  onClick={() => {
                    if (row.id) {
                      const templateId = row.id;
                      openDialog({
                        title: `Delete "${row.name}"`,
                        content: 'Are you sure you want to delete this message template?',
                        onConfirm: () => handleMessageTemplateDelete(templateId),
                      });
                    }
                  }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </>
            );
          }}
          sorting={{
            value: sortField,
            onChange: (s: GridSortModel) => {
              if (s[0]) {
                setSortField(`${s[0]?.field}_${s[0]?.sort}`);
              }
            },
          }}
          pagination={{
            page: page,
            pageSize: pageSize,
            totalCount: messageTemplates.count,
            onChange: (p: GridPaginationModel) => {
              setPage(p.page);
            },
          }}
        />
      </Box>
      <ConfirmationDialog />
    </>
  );
};

export default MessageTemplates;
